import { useEffect, useMemo } from 'react'

import * as Sentry from '@sentry/nextjs'

import { useFacilityTypesQuery } from '@/graphql/generated/hooks'
import { FacilityTypesQuery } from '@/graphql/generated/operations'
import { FacilityFilterInput } from '@/graphql/generated/schemas'
import { MIN_CHAR_COUNT } from '@/utils/forms/validation'

interface UseFacilitySearchIProps {
  searchQuery: string
  filter?: FacilityFilterInput
  skip?: boolean
}

interface UseFacilitySearchResultIProps {
  facilitiesData: FacilityTypesQuery
  isFacilitiesLoading: boolean
  isError: boolean
}

/**
 * Searches facilities by name, short name, and address, each within its own
 * 100-row query. `facilityTypes.gql` caps every query at `last: 100` — if
 * name/short-name and address matches shared one query, a broad address
 * query at high facility volume (e.g. AT&T scale) could fill that cap with
 * address-only rows and push genuine name/short-name matches out before any
 * client-side ranking ever sees them. Fetching name/short-name matches in
 * their own query guarantees they're never displaced by address noise;
 * address-only matches (deduped against the name/short-name set) are
 * appended after, so they're already correctly ordered before
 * getIncidentFacilityOptionsWithAddress's ranking runs.
 *
 * @see {@link https://hivewatch.atlassian.net/browse/ENG-31995|ENG-31995}
 */
export const useFacilitySearch = ({
  searchQuery,
  filter,
  skip = false,
}: UseFacilitySearchIProps): UseFacilitySearchResultIProps => {
  const isSearching = searchQuery?.length >= MIN_CHAR_COUNT

  // Browse mode: no search text yet, just list facilities.
  const {
    data: defaultData,
    loading: isLoadingDefault,
    error: defaultError,
  } = useFacilityTypesQuery({
    fetchPolicy: 'network-only',
    skip: skip || isSearching,
    variables: { filter },
  })

  const {
    data: nameMatchesData,
    previousData: previousNameMatchesData,
    loading: isLoadingNameMatches,
    error: nameMatchesError,
  } = useFacilityTypesQuery({
    fetchPolicy: 'network-only',
    skip: skip || !isSearching,
    variables: {
      filter: {
        ...filter,
        OR: [{ nameLike: searchQuery }, { shortNameLike: searchQuery }],
      },
    },
  })

  const {
    data: addressMatchesData,
    previousData: previousAddressMatchesData,
    loading: isLoadingAddressMatches,
    error: addressMatchesError,
  } = useFacilityTypesQuery({
    fetchPolicy: 'network-only',
    skip: skip || !isSearching,
    variables: {
      filter: { ...filter, addressLike: searchQuery },
    },
  })

  // A failed query silently becomes an empty edge list below (`|| []`), which
  // would otherwise read as "no matches" rather than "search failed" — report
  // it and let consumers surface something actionable instead of a false
  // empty state.
  const searchError = isSearching
    ? nameMatchesError || addressMatchesError
    : defaultError

  useEffect(() => {
    if (searchError) {
      Sentry.captureException(searchError, {
        tags: { action: 'search', feature: 'facility_search' },
      })
    }
  }, [searchError])

  const facilitiesData = useMemo(() => {
    if (!isSearching) return defaultData

    // A `network-only` query clears `data` to `undefined` the instant its
    // variables change, before the new response arrives — so refining an
    // already-settled search (not the initial browse→search transition)
    // would otherwise flash the results list empty on every keystroke.
    // Falling back to `previousData` keeps the last-known results on screen
    // until the new ones land.
    const nameData = nameMatchesData ?? previousNameMatchesData
    const addressData = addressMatchesData ?? previousAddressMatchesData

    const nameEdges = nameData?.facilities?.edges || []
    const nameIds = new Set(nameEdges.map((edge) => edge?.node?.id))
    const addressOnlyEdges = (addressData?.facilities?.edges || []).filter(
      (edge) => !nameIds.has(edge?.node?.id)
    )

    return {
      facilities: { edges: [...nameEdges, ...addressOnlyEdges] },
    }
  }, [
    isSearching,
    defaultData,
    nameMatchesData,
    previousNameMatchesData,
    addressMatchesData,
    previousAddressMatchesData,
  ])

  return {
    facilitiesData,
    isFacilitiesLoading: isSearching
      ? isLoadingNameMatches || isLoadingAddressMatches
      : isLoadingDefault,
    isError: Boolean(searchError),
  }
}
